// shell/components/table/columns/icon-text-column.ts

import { IconTextColumnType, IconConfig } from '../types';
import { TextColumnRenderer } from './text-column';
import { CellRenderContext, CellUpdateType, CellRenderResult } from './column-base';

export class IconTextColumnRenderer<T = unknown> extends TextColumnRenderer<T> {
    constructor(private iconConfig: IconTextColumnType<T>) {
        super({
            kind: 'text',
            converter: iconConfig.converter,
            textColor: iconConfig.textColor
        });
    }

    getGridCellClass(): string {
        return 'cs-column-icon-text';
    }

    getTableCellClass(): string {
        return 'cs-column-icon-text';
    }

    buildGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement): HTMLElement {
        const wrapper = this.buildIconTextWrapper(ctx);

        // Cache elements for efficient updates
        this.cacheElement(cellWrapper, 'wrapper', wrapper);

        if (this.shouldShadeInGrid(ctx)) {
            this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }

        return wrapper;
    }

    buildTableCell(ctx: CellRenderContext<T>, cell: HTMLElement): HTMLElement {
        const wrapper = this.buildIconTextWrapper(ctx);

        this.cacheElement(cell, 'wrapper', wrapper);

        if (this.shouldShadeInTable(ctx)) {
            this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
        }

        return wrapper;
    }

    private buildIconTextWrapper(ctx: CellRenderContext<T>): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'cs-column-icon-text__wrapper';

        const textEl = document.createElement('span');
        textEl.className = 'cs-column-icon-text__text';
        textEl.textContent = this.formatValue(ctx.value, this.iconConfig.converter, ctx.item);

        if (this.iconConfig.textColor) {
            try {
                const color = this.iconConfig.textColor(ctx.value, ctx.item);
                if (color) textEl.style.color = color;
            } catch (e) {
                console.error('Text color callback error:', e);
            }
        }

        const icon = this.buildIcon(ctx);

        // Use 'start'/'end' for RTL support (CSS handles via flexbox order or logical properties)
        const placeAtEnd = this.iconConfig.placement === 'end';
        wrapper.dataset.iconPlacement = placeAtEnd ? 'end' : 'start';

        if (placeAtEnd) {
            wrapper.appendChild(textEl);
            wrapper.appendChild(icon);
        } else {
            wrapper.appendChild(icon);
            wrapper.appendChild(textEl);
        }

        return wrapper;
    }

    private buildIcon(ctx: CellRenderContext<T>): HTMLElement {
        let iconConfig: IconConfig;
        try {
            iconConfig = this.iconConfig.icon(ctx.value, ctx.item);
        } catch (e) {
            console.error('Icon callback error:', e);
            iconConfig = { name: 'mdi:help-circle-outline' };
        }

        const icon = document.createElement('span');
        icon.className = 'iconify cs-column-icon-text__icon';
        icon.dataset.icon = iconConfig.name;

        if (iconConfig.size) {
            icon.style.fontSize = `${iconConfig.size}px`;
        }

        if (iconConfig.color) {
            icon.style.color = iconConfig.color;
        }

        if (iconConfig.cssClass) {
            const classes = Array.isArray(iconConfig.cssClass)
                ? iconConfig.cssClass
                : [iconConfig.cssClass];
            icon.classList.add(...classes);
        }

        return icon;
    }

    updateGridCell(ctx: CellRenderContext<T>, cellWrapper: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        if (type === CellUpdateType.Shades) {
            if (this.shouldShadeInGrid(ctx)) {
                this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
            }
            return { contentElement: contentEl };
        }

        // Full update
        if (contentEl) {
            // Update icon
            const icon = contentEl.querySelector('.cs-column-icon-text__icon') as HTMLElement;
            if (icon) {
                try {
                    const iconConfig = this.iconConfig.icon(ctx.value, ctx.item);
                    icon.dataset.icon = iconConfig.name;
                    icon.style.fontSize = iconConfig.size ? `${iconConfig.size}px` : '';
                    icon.style.color = iconConfig.color || '';
                } catch (e) {
                    console.error('Icon callback error:', e);
                }
            }

            // Update text
            const textEl = contentEl.querySelector('.cs-column-icon-text__text') as HTMLElement;
            if (textEl) {
                const text = this.formatValue(ctx.value, this.iconConfig.converter, ctx.item);
                textEl.textContent = text;

                if (this.iconConfig.textColor) {
                    try {
                        const color = this.iconConfig.textColor(ctx.value, ctx.item);
                        textEl.style.color = color || '';
                    } catch (e) {
                        console.error('Text color callback error:', e);
                    }
                }
            }
        }

        if (this.shouldShadeInGrid(ctx)) {
            this.applyShade(cellWrapper, ctx.shadeRank, ctx.heatMapBounds);
        }

        return { contentElement: contentEl };
    }

    updateTableCell(ctx: CellRenderContext<T>, cell: HTMLElement, contentEl: HTMLElement | null, type: CellUpdateType): CellRenderResult {
        const result = this.updateGridCell(ctx, cell, contentEl, type);

        if (type === CellUpdateType.Shades && this.shouldShadeInTable(ctx)) {
            this.applyShade(cell, ctx.shadeRank, ctx.heatMapBounds);
        }

        return result;
    }
}
